import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProducts, deleteProduct, updateProduct, createManualSale, getCustomers } from '../../services/adminService';
import { normalizeInstagram } from '../../utils/validation';
import { supabase } from '../../lib/supabase';
import PaymentProofUploadField from '../../components/PaymentProofUploadField';
import DirectSaleItemsField, { saleItemsTotal } from './DirectSaleItemsField';

const formatPrice = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const STATUS_OPTIONS = [
  { value: '',          label: 'Todos' },
  { value: 'available', label: 'En stock' },
  { value: 'preorder',  label: 'Preventa' },
  { value: 'soldout',   label: 'Agotado' },
  { value: 'hidden',    label: 'Ocultos' },
];

const STOCK_CONFIG = {
  available: { label: 'En stock', color: '#28a745', bg: '#eaf7ed' },
  preorder:  { label: 'Preventa', color: '#2ca9e1', bg: '#e8f6fd' },
  soldout:   { label: 'Agotado',  color: '#dc3545', bg: '#fdecea' },
};

function stockStatus(product) {
  if (product.isPreorder) return 'preorder';
  if (product.isSoldOut)  return 'soldout';
  return 'available';
}

const StockBadge = ({ product }) => {
  const key = stockStatus(product);
  const cfg = STOCK_CONFIG[key];
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
};

// Customer <select> value for "type a name" (no customer account)
const CUSTOM_CUSTOMER = '__custom__';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

const PAGE_LIMIT = 20;

const AdminProductList = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts]     = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [status, setStatus]           = useState('');
  const [categoryId, setCategoryId]   = useState('');
  const [brandId, setBrandId]         = useState('');
  const [categories, setCategories]   = useState([]);
  const [brands, setBrands]           = useState([]);
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [saleModal, setSaleModal]   = useState(null);
  // Products in the sale: [{ id, name, image, amount }] — starts with the row's product
  const [saleItems, setSaleItems]   = useState([]);
  const [saleForm, setSaleForm]     = useState({
    notes: '', customerId: '', customerName: '', customerInstagram: '',
    paymentType: 'full', // 'full' | 'full_pending' | 'partial'
    fulfillmentStatus: 'entregado',
    mercadoPagoUrl: '',
    initialPaymentAmount: '',
    initialPaymentProof: null, // { path, filename }
  });
  const [saleProofUploading, setSaleProofUploading] = useState(false);
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleError, setSaleError]   = useState(null);
  const [saleCustomers, setSaleCustomers] = useState([]);

  const [refreshKey, setRefreshKey]         = useState(0);
  const [reorderMode, setReorderMode]       = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderProducts, setReorderProducts] = useState([]);
  const [savingOrder, setSavingOrder]       = useState(false);
  const [dragIndex, setDragIndex]           = useState(null);
  const [overIndex, setOverIndex]           = useState(null);

  const filtersActive = Boolean(search || status || categoryId || brandId);

  useEffect(() => {
    supabase
      .from('categories')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (error) {
          console.error('[AdminProductList] failed to load categories:', error);
          return;
        }
        if (data) setCategories(data);
      });
  }, []);

  useEffect(() => {
    supabase
      .from('brands')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (error) {
          console.error('[AdminProductList] failed to load brands:', error);
          return;
        }
        if (data) setBrands(data);
      });
  }, []);

  // Debounce search 400 ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => { setPage(1); }, [categoryId]);
  useEffect(() => { setPage(1); }, [brandId]);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (status)          params.status      = status;
    if (categoryId)      params.category_id = categoryId;
    if (brandId)         params.brand_id    = brandId;
    if (debouncedSearch) params.search      = debouncedSearch;

    getProducts(session.access_token, params)
      .then((data) => {
        if (cancelled) return;
        setProducts(data.products || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page, status, categoryId, brandId, debouncedSearch, refreshKey]);

  const handleToggleVisibility = async (product) => {
    setTogglingId(product.id);
    try {
      const updated = await updateProduct(session.access_token, product.id, { isHidden: !product.isHidden });
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, isHidden: updated.product.isHidden } : p));
    } catch (err) {
      alert(`Error al cambiar visibilidad: ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(product.id);
    try {
      await deleteProduct(session.access_token, product.id);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      setTotal((t) => t - 1);
    } catch (err) {
      alert(`Error al eliminar: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const enterReorderMode = async () => {
    if (filtersActive || !session?.access_token) return;
    setReorderLoading(true);
    setError(null);
    try {
      const data = await getProducts(session.access_token, { page: 1, limit: 100 });
      setReorderProducts(data.products || []);
      setReorderMode(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setReorderLoading(false);
    }
  };

  const exitReorderMode = () => {
    setReorderMode(false);
    setReorderProducts([]);
    setPage(1);
    setRefreshKey((k) => k + 1);
  };

  const persistOrder = async (orderedList) => {
    const updates = orderedList
      .map((p, idx) => ({ id: p.id, displayOrder: idx + 1, changed: p.displayOrder !== idx + 1 }))
      .filter((u) => u.changed);
    if (updates.length === 0) return;

    setSavingOrder(true);
    try {
      await Promise.all(
        updates.map((u) => updateProduct(session.access_token, u.id, { displayOrder: u.displayOrder }))
      );
      setReorderProducts((prev) => prev.map((p, idx) => ({ ...p, displayOrder: idx + 1 })));
    } catch (err) {
      alert(`Error al guardar el nuevo orden: ${err.message}`);
      // Reload from the server so the visible order matches what's actually saved.
      try {
        const data = await getProducts(session.access_token, { page: 1, limit: 100 });
        setReorderProducts(data.products || []);
      } catch {
        // Best-effort recovery only — leave the current (possibly stale) list visible.
      }
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (idx) => {
    if (savingOrder) return;
    setDragIndex(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (idx !== overIndex) setOverIndex(idx);
  };

  const handleDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...reorderProducts];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setReorderProducts(next);
    setDragIndex(null);
    setOverIndex(null);
    persistOrder(next);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const openSaleModal = (product) => {
    setSaleModal(product);
    setSaleItems([{
      id: product.id,
      name: product.name,
      image: product.image || null,
      amount: product.price != null ? String(product.price) : '',
    }]);
    setSaleForm({
      notes: '',
      customerId: '',
      customerName: '',
      customerInstagram: '',
      paymentType: 'full',
      fulfillmentStatus: 'entregado',
      mercadoPagoUrl: '',
      initialPaymentAmount: '',
      initialPaymentProof: null,
    });
    setSaleProofUploading(false);
    setSaleError(null);
    if (session?.access_token) {
      getCustomers(session.access_token, { limit: 100, page: 1 })
        .then((data) => setSaleCustomers(data.customers || []))
        .catch(() => {});
    }
  };

  const handleRegisterSale = async (e) => {
    e.preventDefault();

    const total = saleItemsTotal(saleItems);
    const isPartial = saleForm.paymentType === 'partial';
    const isFullPending = saleForm.paymentType === 'full_pending';
    const initial = saleForm.initialPaymentAmount ? parseFloat(saleForm.initialPaymentAmount) : 0;

    if (isPartial && initial > 0) {
      if (initial > total + 0.01) {
        setSaleError('El pago inicial no puede ser mayor al total.');
        return;
      }
      if (!saleForm.initialPaymentProof?.path) {
        setSaleError('Sube el comprobante del pago inicial.');
        return;
      }
    }

    const customerInstagram = normalizeInstagram(saleForm.customerInstagram);
    if (customerInstagram === undefined) {
      setSaleError('El usuario de Instagram no es válido.');
      return;
    }

    const isCustomCustomer = saleForm.customerId === CUSTOM_CUSTOMER;
    if (isCustomCustomer && !saleForm.customerName.trim()) {
      setSaleError('Escribe el nombre del cliente.');
      return;
    }

    setSaleLoading(true);
    setSaleError(null);
    try {
      await createManualSale(session.access_token, {
        items: saleItems.map((i) => ({ productId: i.id, amount: parseFloat(i.amount) })),
        notes: saleForm.notes || undefined,
        customerId: (!isCustomCustomer && saleForm.customerId) || undefined,
        customerName: isCustomCustomer ? saleForm.customerName.trim() : undefined,
        customerInstagram: customerInstagram || undefined,
        allowsPartialPayment: (isPartial || isFullPending) || undefined,
        isFullPending: isFullPending || undefined,
        fulfillmentStatus: !isPartial && !isFullPending ? saleForm.fulfillmentStatus : undefined,
        mercadoPagoUrl: isPartial && initial <= 0 && saleForm.mercadoPagoUrl
          ? saleForm.mercadoPagoUrl.trim()
          : undefined,
        initialPaymentAmount: isPartial && initial > 0 ? initial : undefined,
        initialPaymentProofPath: isPartial && initial > 0 ? saleForm.initialPaymentProof.path : undefined,
        initialPaymentProofFilename: isPartial && initial > 0 ? saleForm.initialPaymentProof.filename : undefined,
      });
      setProducts((prev) =>
        prev.map((p) => saleItems.some((i) => i.id === p.id) ? { ...p, isSoldOut: true, quantity: 0 } : p)
      );
      setSaleModal(null);
    } catch (err) {
      setSaleError(err.message || 'Error al registrar la venta.');
    } finally {
      setSaleLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Productos
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} producto{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filters + new button */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar por nombre o artista..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: '220px', flex: '1' }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {categories.length > 0 && (
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">Todas las categorías</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        )}
        {brands.length > 0 && (
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">Todas las marcas</option>
            {brands.map((b) => (
              <option key={b.id} value={String(b.id)}>{b.name}</option>
            ))}
          </select>
        )}
        <button
          onClick={enterReorderMode}
          disabled={filtersActive || reorderLoading}
          title={filtersActive ? 'Limpia los filtros para reordenar el catálogo completo' : 'Arrastra los productos para reordenarlos'}
          style={{
            padding: '0.5rem 1.25rem',
            background: 'white',
            color: filtersActive ? '#bbb' : '#333',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: filtersActive || reorderLoading ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {reorderLoading ? 'Cargando...' : '⇅ Reordenar'}
        </button>
        <button
          onClick={() => navigate('/admin/products/new')}
          style={{
            padding: '0.5rem 1.25rem',
            background: '#E4405F',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Nuevo producto
        </button>
      </div>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      {reorderMode ? (
        <>
          {/* Reorder mode banner */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
            marginBottom: '1rem', padding: '0.75rem 1rem',
            background: '#f0f9ff', border: '1px solid #d3ecfa', borderRadius: '8px',
          }}>
            <span style={{ color: '#333', fontSize: '0.88rem' }}>
              Arrastra las filas para cambiar el orden en el catálogo. {savingOrder ? 'Guardando…' : 'Los cambios se guardan automáticamente.'}
            </span>
            <button
              onClick={exitReorderMode}
              style={{
                marginLeft: 'auto', padding: '0.4rem 1rem', background: '#2ca9e1', color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Listo
            </button>
          </div>

          {/* Reorder table */}
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['', 'Orden', 'Imagen', 'Nombre', 'Categoría'].map((h) => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reorderProducts.map((product, idx) => (
                    <tr
                      key={product.id}
                      draggable={!savingOrder}
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={() => handleDrop(idx)}
                      onDragEnd={handleDragEnd}
                      style={{
                        borderBottom: '1px solid #f8f8f8',
                        opacity: dragIndex === idx ? 0.4 : product.isHidden ? 0.5 : 1,
                        background: overIndex === idx && dragIndex !== idx ? '#f0f9ff' : 'transparent',
                        cursor: savingOrder ? 'default' : 'grab',
                      }}
                    >
                      <td style={{ padding: '0.75rem 0.5rem 0.75rem 1rem', color: '#bbb', fontSize: '1rem', userSelect: 'none' }}>⠿</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#333', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '0.6rem 1rem' }}>
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{ width: 44, height: 44, borderRadius: 6, background: '#f0f0f0' }} />
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333' }}>{product.name}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{product.category || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
      <>
      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Orden', 'Imagen', 'Nombre', 'Artista', 'Precio', 'Costo', 'Stock', 'Estado', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>
                    No se encontraron productos.
                  </td>
                </tr>
              ) : products.map((product) => (
                <tr key={product.id} style={{ borderBottom: '1px solid #f8f8f8', opacity: product.isHidden ? 0.5 : 1 }}>
                  <td style={{ padding: '0.75rem 1rem', color: product.displayOrder == null ? '#bbb' : '#333', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {product.displayOrder ?? '—'}
                  </td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ width: 44, height: 44, borderRadius: 6, background: '#f0f0f0' }} />
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333', maxWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {product.name}
                      {product.isHidden && (
                        <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: '600', color: '#666', background: '#eee', whiteSpace: 'nowrap' }}>
                          Oculto
                        </span>
                      )}
                    </div>
                    {product.series && (
                      <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 2 }}>{product.series}</div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{product.artist || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#E4405F', whiteSpace: 'nowrap' }}>
                    {formatPrice(product.price)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {product.originalPrice != null ? formatPrice(product.originalPrice) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{product.quantity}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <StockBadge product={product} />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleVisibility(product)}
                        disabled={togglingId === product.id}
                        title={product.isHidden ? 'Mostrar en tienda' : 'Ocultar de tienda'}
                        style={{
                          background: 'none', border: 'none',
                          color: togglingId === product.id ? '#ccc' : product.isHidden ? '#28a745' : '#888',
                          cursor: togglingId === product.id ? 'default' : 'pointer',
                          fontSize: '0.85rem', fontWeight: '500', padding: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {togglingId === product.id ? '...' : product.isHidden ? 'Mostrar' : 'Ocultar'}
                      </button>
                      <Link
                        to={`/admin/products/${product.id}`}
                        style={{ color: '#2ca9e1', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                      >
                        Editar →
                      </Link>
                      {!product.isSoldOut && (
                        <button
                          onClick={() => openSaleModal(product)}
                          style={{
                            background: 'none', border: 'none',
                            color: '#28a745', cursor: 'pointer',
                            fontSize: '0.85rem', fontWeight: '500', padding: 0, whiteSpace: 'nowrap',
                          }}
                        >
                          Venta directa
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(product)}
                        disabled={deletingId === product.id}
                        style={{
                          background: 'none', border: 'none',
                          color: deletingId === product.id ? '#ccc' : '#dc3545',
                          cursor: deletingId === product.id ? 'default' : 'pointer',
                          fontSize: '0.85rem', fontWeight: '500', padding: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {deletingId === product.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer', fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>Página {page} de {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer', fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
      </>
      )}

      {/* Venta directa modal */}
      {saleModal && (
        <div
          onClick={() => !saleLoading && setSaleModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              padding: '2rem', width: '100%', maxWidth: '480px',
              maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto',
            }}
          >
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem', color: '#333' }}>
              Venta directa
            </h2>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.88rem', color: '#888' }}>
              {saleItems.length === 1 ? saleItems[0].name : `${saleItems.length} productos en un solo pedido`}
            </p>

            <form onSubmit={handleRegisterSale}>
              {saleError && (
                <p style={{ color: '#dc3545', fontSize: '0.88rem', marginBottom: '1rem' }}>{saleError}</p>
              )}

              <DirectSaleItemsField
                items={saleItems}
                onChange={setSaleItems}
                token={session?.access_token}
                disabled={saleLoading}
              />

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Notas (opcional)
                </label>
                <textarea
                  rows={2}
                  value={saleForm.notes}
                  onChange={(e) => setSaleForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ej. Pago en efectivo, entrega en tienda..."
                  style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Tipo de pago
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {[
                    { value: 'full',         label: 'Pago completo',         desc: 'El cliente ya pagó el total.' },
                    { value: 'full_pending',  label: 'Pago pendiente total',  desc: 'El cliente aún no ha pagado nada.' },
                    { value: 'partial',       label: 'Pago parcial (abonos)', desc: 'El cliente pagará en partes.' },
                  ].map(({ value, label, desc }) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer',
                        padding: '0.5rem 0.75rem', borderRadius: '8px',
                        background: saleForm.paymentType === value ? '#f0f9ff' : '#f8f9fa',
                        border: `1.5px solid ${saleForm.paymentType === value ? '#2ca9e1' : 'transparent'}`,
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentType"
                        value={value}
                        checked={saleForm.paymentType === value}
                        onChange={() => setSaleForm((f) => ({ ...f, paymentType: value }))}
                        style={{ marginTop: '2px' }}
                      />
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>{label}</div>
                        <div style={{ fontSize: '0.78rem', color: '#888' }}>{desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {saleForm.paymentType === 'full' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Estado del pedido
                  </label>
                  <select
                    value={saleForm.fulfillmentStatus}
                    onChange={(e) => setSaleForm((f) => ({ ...f, fulfillmentStatus: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}
                  >
                    <option value="entregado">Entregado</option>
                    <option value="enviado">Enviado</option>
                    <option value="recoleccion">Recolección</option>
                    <option value="en_preparacion">En preparación</option>
                  </select>
                </div>
              )}

              {saleForm.paymentType === 'partial' && (() => {
                const initial = parseFloat(saleForm.initialPaymentAmount) || 0;
                const hasInitial = initial > 0;
                return (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem' }}>
                      Pago inicial recibido (opcional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={saleForm.initialPaymentAmount}
                      onChange={(e) => setSaleForm((f) => ({ ...f, initialPaymentAmount: e.target.value }))}
                      placeholder="0.00"
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                    />
                    <p style={{ margin: '0.35rem 0 0.75rem', fontSize: '0.78rem', color: '#888' }}>
                      Si el cliente ya pagó una parte (efectivo o transferencia), regístralo aquí.
                    </p>

                    {hasInitial ? (
                      <PaymentProofUploadField
                        userId={user?.id}
                        purchaseId={`new-${saleModal.id}`}
                        value={saleForm.initialPaymentProof}
                        onChange={(proof) => setSaleForm((f) => ({ ...f, initialPaymentProof: proof }))}
                        onUploadingChange={setSaleProofUploading}
                      />
                    ) : (
                      <>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem' }}>
                          Link de Mercado Pago (opcional)
                        </label>
                        <input
                          type="url"
                          value={saleForm.mercadoPagoUrl}
                          onChange={(e) => setSaleForm((f) => ({ ...f, mercadoPagoUrl: e.target.value }))}
                          placeholder="https://mpago.la/..."
                          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                        />
                        <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#888' }}>
                          Si lo dejas en blanco, el cliente solo verá transferencia bancaria.
                        </p>
                      </>
                    )}
                  </div>
                );
              })()}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Cliente (opcional)
                </label>
                <select
                  value={saleForm.customerId}
                  onChange={(e) => setSaleForm((f) => ({ ...f, customerId: e.target.value }))}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', cursor: 'pointer', color: saleForm.customerId ? '#333' : '#999' }}
                >
                  <option value="">— Asignar después —</option>
                  <option value={CUSTOM_CUSTOMER}>✏️ Otro (escribir nombre)</option>
                  {saleCustomers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name ? `${c.name} (${c.email})` : c.email}
                    </option>
                  ))}
                </select>
                {saleForm.customerId === CUSTOM_CUSTOMER && (
                  <>
                    <input
                      type="text"
                      autoFocus
                      maxLength={120}
                      value={saleForm.customerName}
                      onChange={(e) => setSaleForm((f) => ({ ...f, customerName: e.target.value }))}
                      placeholder="Nombre del cliente"
                      aria-label="Nombre del cliente"
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: '0.5rem' }}
                    />
                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: '#999' }}>
                      Solo se guarda en el pedido (sin cuenta ni correo).
                    </p>
                  </>
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Instagram (opcional)
                </label>
                <input
                  type="text"
                  value={saleForm.customerInstagram}
                  onChange={(e) => setSaleForm((f) => ({ ...f, customerInstagram: e.target.value }))}
                  placeholder="@usuario"
                  autoCapitalize="none"
                  spellCheck={false}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setSaleModal(null)}
                  disabled={saleLoading}
                  style={{
                    padding: '0.6rem 1.25rem', background: 'none',
                    border: '1px solid #ddd', borderRadius: '8px',
                    fontSize: '0.9rem', cursor: saleLoading ? 'default' : 'pointer', color: '#555',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saleLoading || saleProofUploading}
                  style={{
                    padding: '0.6rem 1.5rem',
                    background: saleLoading || saleProofUploading ? '#ccc' : '#28a745',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '0.9rem', fontWeight: '600',
                    cursor: saleLoading || saleProofUploading ? 'default' : 'pointer',
                  }}
                >
                  {saleLoading ? 'Registrando...' : saleProofUploading ? 'Subiendo comprobante...' : 'Confirmar venta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductList;
